import {addHours} from 'date-fns';
import {BigCalendar, BigCalendarEvent, BigCalendarUser} from 'platform/big-calendar';
import {Button, ButtonGroup, DialogFooter} from 'platform/components';
import {Box, VStack, ThemeColorPath} from 'platform/foundation';

import {useMemo, useState} from 'react';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {TestDriveResponseBody, useListTestDriveV2Query} from '@dms/api/sales';
import {GetVehicleApiResponseV2} from '@dms/api/shared';
import {GetVehicleApiResponse} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {VehicleWidget, VehicleWidgetData} from '@dms/shared';

import {EMPTY_PLACEHOLDER, parseDate} from 'shared/web';

const DEFAULT_TEST_DRIVE_DURATION_HOURS = 1;

function checkEventsOverlap(event1: BigCalendarEvent, event2: BigCalendarEvent): boolean {
  return event1.start < event2.end && event2.start < event1.end;
}

function transformTestDriveToCalendarEvent(testDrive: TestDriveResponseBody): BigCalendarEvent {
  const scheduledDate = parseDate(testDrive.scheduledAt);
  const endDate = testDrive.returnedAt ? parseDate(testDrive.returnedAt) : scheduledDate;

  const eventEndDate = testDrive.returnedAt
    ? endDate
    : parseDate(scheduledDate.getTime() + 60 * 60 * 1000); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible

  const customerName =
    testDrive.customerTradeName ||
    `${testDrive.customerFoundingPersonFirstName || ''} ${testDrive.customerFoundingPersonLastName || ''}`.trim() ||
    EMPTY_PLACEHOLDER;

  return {
    id: testDrive.id,
    title: customerName,
    start: scheduledDate,
    end: eventEndDate,
    color: testDrive.state === 'completed' ? 'palettes.neutral.50.100' : 'palettes.blue.50.100',
    alternative: testDrive.state === 'completed' || testDrive.state === 'cancelled',
    isReadOnly: false,
  };
}

export interface CalendarFormProps {
  vehicle: GetVehicleApiResponse | GetVehicleApiResponseV2;
  onConfirm?: (data: {vehicleId: string; start: Date; end: Date}) => void;
  onDiscard?: () => void;
  onChangeVehicle?: () => void;
}

export function CalendarForm(props: CalendarFormProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState<{start: Date; end: Date} | null>(null);

  const {data: userData} = useGetCurrentUserInfoQuery();
  const {data: testDrives = []} = useListTestDriveV2Query({
    vehicleId: props.vehicle.id,
  });

  const currentUser: BigCalendarUser = useMemo(() => {
    if (!userData) {
      return {
        id: 'unknown',
        email: EMPTY_PLACEHOLDER,
        fullName: EMPTY_PLACEHOLDER,
      };
    }

    const fullName =
      [userData.firstName, userData.lastName].filter(Boolean).join(' ') || EMPTY_PLACEHOLDER;

    return {
      id: userData.id,
      email: userData.email,
      fullName,
    };
  }, [userData]);

  const calendarEvents = useMemo(() => {
    const testDriveEvents: BigCalendarEvent[] = testDrives.map(transformTestDriveToCalendarEvent);

    const eventsWithOverlapDetection = testDriveEvents.map((event): BigCalendarEvent => {
      const earlierOverlappingEventsCount = testDriveEvents.filter((otherEvent) => {
        if (otherEvent.id === event.id) {
          return false;
        }

        const eventsOverlap = checkEventsOverlap(event, otherEvent);
        const otherEventStartsEarlier = otherEvent.start < event.start;

        return eventsOverlap && otherEventStartsEarlier;
      }).length;

      if (earlierOverlappingEventsCount === 1) {
        return {
          ...event,
          color: 'palettes.orange.50.100' as ThemeColorPath,
        };
      }

      return event;
    });

    if (selectedSlot) {
      const selectedEvent: BigCalendarEvent = {
        id: 'selected-slot',
        title: i18n.t('entity.testDrive.labels.newTestDrive'),
        start: selectedSlot.start,
        end: selectedSlot.end,
        color: 'palettes.green.50.100' as ThemeColorPath,
      };

      return [...eventsWithOverlapDetection, selectedEvent];
    }

    return eventsWithOverlapDetection;
  }, [testDrives, selectedSlot]);

  const handleNavigate = (date: Date) => {
    setCurrentDate(date);
  };

  const handleDateClick = (date: Date) => {
    const start = date;
    const end = addHours(date, DEFAULT_TEST_DRIVE_DURATION_HOURS);

    setSelectedSlot({start, end});
  };

  const handleEventClick = (event: BigCalendarEvent) => {
    if (event.id === 'selected-slot') {
      return;
    }

    setSelectedSlot({start: event.start, end: event.end});
  };

  const handleEventDragMove = (event: BigCalendarEvent, start: Date, end: Date): Promise<void> => {
    if (event.id === 'selected-slot') {
      setSelectedSlot({start, end});
    }

    return Promise.resolve();
  };

  const handleConfirm = () => {
    if (selectedSlot && props.onConfirm) {
      props.onConfirm({
        vehicleId: props.vehicle.id,
        start: selectedSlot.start,
        end: selectedSlot.end,
      });
    }
  };

  const handleDiscard = () => {
    setSelectedSlot(null);
    if (props.onDiscard) {
      props.onDiscard();
    }
  };

  const vehicleWithActions = {
    ...props.vehicle,
    actions: props.onChangeVehicle
      ? [
          {
            type: 'button',
            variant: 'link',
            leftIcon: 'action/cached',
            title: i18n.t('entity.testDrive.labels.changeVehicle'),
            onClick: props.onChangeVehicle,
          },
        ]
      : undefined,
  };

  return (
    <Box paddingBottom={12}>
      <VStack spacing={4}>
        <VehicleWidget
          vehicle={vehicleWithActions as VehicleWidgetData}
          data-testid="test-drive-vehicle-widget"
        />

        <Box width="100%" height={160} overflowY="scroll">
          <BigCalendar
            currentUser={currentUser}
            events={calendarEvents}
            currentDate={currentDate}
            defaultView="week"
            allowedViews={['week']}
            onNavigate={handleNavigate}
            onDateClick={handleDateClick}
            onEventClick={handleEventClick}
            onEventDragMove={handleEventDragMove}
            data-testid="test-drive-calendar"
          />
        </Box>

        <DialogFooter>
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={handleDiscard}
            />
            <Button
              title={i18n.t('general.actions.save')}
              onClick={handleConfirm}
              isDisabled={!selectedSlot}
            />
          </ButtonGroup>
        </DialogFooter>
      </VStack>
    </Box>
  );
}
